import { useIntl, FormattedMessage } from 'react-intl';
import { useSnackbar } from '~/containers/Snackbar/SnackbarContext';
import { useTheme } from '@mui/material';
import moment from 'moment';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';

import ContentView from '~/components/ContentView';
import generalMessages from '~/containers/App/messages';
import { useUpdateEvent } from '~/hooks/api/useUpdateEvent';
import { useEvent } from '~/hooks/api/useEvent';
import EventForm from './EventForm';
import messages from './messages';
import { getRouteApi, useNavigate } from '@tanstack/react-router';

const route = getRouteApi('/experiences/$experienceId/events/$eventId/edit');

export const EditEvent = () => {
  const intl = useIntl();
  const { showSnackbar } = useSnackbar();
  const theme = useTheme();
  const navigate = useNavigate();
  const { experienceId, eventId } = route.useParams();
  const experienceIdNum = parseInt(experienceId, 10);
  const eventIdNum = parseInt(eventId, 10);

  const { data: event, isLoading } = useEvent({
    experienceId: experienceIdNum,
    eventId: eventIdNum,
  });

  const gotoExperienceDetails = () => {
    navigate({ to: `/experiences/${experienceIdNum}` });
  };

  const updateEventMutation = useUpdateEvent({
    onSuccess() {
      showSnackbar({
        message: intl.formatMessage(messages.editEventSuccess),
      });
      gotoExperienceDetails();
    },
    onError() {
      showSnackbar({
        message: intl.formatMessage(messages.editEventFailure),
      });
    },
  });

  const handleSubmit = ({
    eventDate,
    eventTime,
    location,
    description,
  }: {
    eventDate: string | null;
    eventTime: string | null;
    location: string;
    description: string;
  }) => {
    if (!eventDate || !eventTime) return;

    updateEventMutation.mutate({
      experienceId: experienceIdNum,
      eventId: eventIdNum,
      body: {
        eventDate: `${eventDate}T${eventTime}:00`,
        location,
        description,
      },
    });
  };

  const initialValues = event
    ? {
        eventDate: moment(event.eventDate).format('YYYY-MM-DD'),
        eventTime: moment(event.eventDate).format('HH:mm'),
        location: event.location,
        description: event.description,
      }
    : undefined;

  return (
    <ContentView
      header={
        <AppBar position="static">
          <Toolbar>
            <IconButton
              color="inherit"
              onClick={gotoExperienceDetails}
              size="large"
              aria-label={intl.formatMessage(messages.back)}
            >
              <CloseIcon />
            </IconButton>
            <Typography variant="h6" style={{ marginLeft: theme.spacing(2) }}>
              <FormattedMessage {...messages.editEventHeader} />
            </Typography>
          </Toolbar>
        </AppBar>
      }
      footer={
        <Toolbar>
          <Button
            disabled={updateEventMutation.isPending}
            color="primary"
            type="submit"
            form="event-form"
          >
            {updateEventMutation.isPending ? (
              <FormattedMessage {...generalMessages.loading} />
            ) : (
              <FormattedMessage {...messages.saveEventButton} />
            )}
          </Button>
        </Toolbar>
      }
      isLoading={isLoading}
    >
      {initialValues && (
        <EventForm onSubmit={handleSubmit} initialValues={initialValues} />
      )}
    </ContentView>
  );
};

export default EditEvent;
